// Territories (`/territories`, WEB_PLAN.md §5.8, Phase 1 = read-only): a port of GroundTeam/Views/Territories/
// TerritoryListView.swift. Scopes All / Mine / Unassigned, search on name, ZIP code and assigned rep, active
// territories first and inactive ones in their own section, each as a card with its color, kind, area, reps and the
// `territoryStats` numbers. Creating and editing territories comes with Phase 2 (the editor and its reassignment run).
//
// Scope and search are kept in the URL query (replace, not push) so Back from a territory returns to the same list.

import { Map as MapIcon, PersonStanding, SearchX, UserRoundX, Users } from 'lucide-react';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useActivities, usePipelines, useRecords, useSettings, useTerritories, useUsers, useViewer } from '@/data/hooks';
import { formatGrouped } from '@/domain/format';
import { byId, fullName, idKey, lookup } from '@/domain/util';
import { RequireFirstSync, ScreenLoading, useDocumentTitle } from '@/features/common';
import { useCan } from '@/session/permissions';
import { EmptyState, PageContainer, PageHeader, SearchField, SectionLabel, SegmentedControl, useTicker, type AvatarPerson } from '@/ui';
import { TerritoryCard } from './TerritoryCard';
import {
  EMPTY_TERRITORY_STATS,
  TERRITORY_SCOPES,
  TERRITORY_SCOPE_TITLES,
  listedTerritories,
  plural,
  scopesFor,
  storesByTerritory,
  territoryListSections,
  territoryStatsMap,
  territorySummary,
  visibleTerritories,
  type TerritoryScope,
} from './territoryModel';

const HOUR = 60 * 60 * 1000;
const NO_REPS: AvatarPerson[] = [];

export default function TerritoriesPage() {
  useDocumentTitle('Territories');
  return (
    <PageContainer>
      <RequireFirstSync what="Territories" fallback={<ScreenLoading layout="cards" label="Loading territories…" />}>
        <TerritoryList />
      </RequireFirstSync>
    </PageContainer>
  );
}

function parseScope(v: string | null, allowed: readonly TerritoryScope[]): TerritoryScope {
  return v && (TERRITORY_SCOPES as readonly string[]).includes(v) && allowed.includes(v as TerritoryScope) ? (v as TerritoryScope) : 'all';
}

function TerritoryList() {
  const can = useCan();
  const viewer = useViewer();
  const settings = useSettings();
  const territories = useTerritories();
  const records = useRecords();
  const activities = useActivities();
  const pipelines = usePipelines();
  const users = useUsers();
  // The "visits in 30 days" window moves with the clock, not only with the data.
  const hour = useTicker(HOUR);

  const [params, setParams] = useSearchParams();
  const scopes = scopesFor(can.isManager);
  const scope = parseScope(params.get('scope'), scopes);
  const query = params.get('q') ?? '';
  const update = (key: 'scope' | 'q', value: string) => {
    const next = new URLSearchParams(params);
    if (value === '' || (key === 'scope' && value === 'all')) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const usersById = useMemo(() => byId(users.values), [users.values]);
  const listed = useMemo(() => listedTerritories(territories.items), [territories.items]);
  const visible = useMemo(
    () => visibleTerritories(listed.territories, { profileId: viewer?.profileId, role: viewer?.role }, settings.allowRepsToCreateTerritories),
    [listed.territories, viewer?.profileId, viewer?.role, settings.allowRepsToCreateTerritories],
  );
  // Stats for every visible territory once per data change (or hour); scope and search only pick from them.
  const stats = useMemo(() => {
    void hour;
    const members = storesByTerritory(visible, records.values);
    return territoryStatsMap(visible, { records: records.values, activities: activities.values, pipelines: pipelines.values, members }, new Date());
  }, [visible, records.values, activities.values, pipelines.values, hour]);
  const sections = useMemo(
    () => territoryListSections(visible, { scope, query, viewerId: viewer?.profileId, users: usersById }),
    [visible, scope, query, viewer?.profileId, usersById],
  );
  const summary = territorySummary(sections.all, (t) => stats.get(idKey(t.id))?.records ?? 0);

  // Avatar lists per territory, kept stable between renders so the memoized cards skip re-rendering while typing.
  const repsByTerritory = useMemo(() => {
    const out = new Map<string, AvatarPerson[]>();
    for (const t of visible) {
      out.set(
        idKey(t.id),
        t.assignedUserIds.flatMap((uid) => {
          const u = lookup(usersById, uid);
          return u ? [{ id: u.id, name: fullName(u) || 'Unnamed', colorHex: u.colorHex }] : [];
        }),
      );
    }
    return out;
  }, [visible, usersById]);

  const card = (t: (typeof visible)[number]) => (
    <li key={t.id}>
      <TerritoryCard
        territory={t}
        stats={stats.get(idKey(t.id)) ?? EMPTY_TERRITORY_STATS}
        reps={repsByTerritory.get(idKey(t.id)) ?? NO_REPS}
        unit={settings.distanceUnit}
        currencyCode={settings.currencyCode}
        newer={listed.newer.has(idKey(t.id))}
      />
    </li>
  );

  return (
    <>
      <PageHeader
        title="Territories"
        subtitle={
          sections.all.length > 0
            ? `${formatGrouped(summary.territories)} ${plural(summary.territories, 'territory', 'territories')} · ${formatGrouped(summary.stores)} ${plural(summary.stores, 'store', 'stores')} · ${formatGrouped(summary.reps)} ${plural(summary.reps, 'rep', 'reps')}`
            : undefined
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedControl
            label="Show territories"
            value={scope}
            onChange={(v) => update('scope', v)}
            options={scopes.map((s) => ({ value: s, label: TERRITORY_SCOPE_TITLES[s] }))}
            size="sm"
          />
          <SearchField
            value={query}
            onValueChange={(v) => update('q', v)}
            label="Search territories, ZIPs, reps"
            size="sm"
            slashShortcut
            className="w-full max-w-sm min-w-[220px] flex-1"
          />
        </div>
      </PageHeader>

      {sections.all.length === 0 ? (
        <ListEmptyState scope={scope} query={query} isManager={can.isManager} />
      ) : (
        <div className="flex flex-col gap-6 pb-8">
          {sections.active.length > 0 && (
            <ul aria-label="Active territories" className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
              {sections.active.map(card)}
            </ul>
          )}
          {sections.inactive.length > 0 && (
            <section aria-labelledby="inactive-territories" className="flex flex-col gap-3">
              <SectionLabel id="inactive-territories">Inactive</SectionLabel>
              <ul className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">{sections.inactive.map(card)}</ul>
            </section>
          )}
        </div>
      )}
    </>
  );
}

/** The iOS empty states, per scope and search. */
function ListEmptyState({ scope, query, isManager }: { scope: TerritoryScope; query: string; isManager: boolean }) {
  if (query.trim() !== '') {
    return <EmptyState icon={<SearchX />} title="No matches" message={`No territory matches "${query.trim()}".`} />;
  }
  if (scope === 'mine') {
    return (
      <EmptyState
        icon={<PersonStanding />}
        title="No territories assigned to you"
        message={isManager ? "Assign yourself from a territory's editor in the Ground Team app." : 'Ask your manager to assign you to a territory.'}
      />
    );
  }
  if (scope === 'unassigned') {
    return <EmptyState icon={<UserRoundX />} color="#29A85C" title="Every territory has a rep" message="Nothing is waiting for an assignment." />;
  }
  return (
    <EmptyState
      icon={isManager ? <MapIcon /> : <Users />}
      title="No territories yet"
      message={
        isManager
          ? 'Draw an area on the map or list ZIP codes in the Ground Team app to carve up your market and auto-assign stores to reps.'
          : "Your manager hasn't created any territories yet."
      }
    />
  );
}
